import { useEffect, useState, useMemo } from 'react'
import CustomSwitch from '../../common/form/CustomSwitch'
import FormDrawer from '../../common/form-drawer'
import { monthNames } from 'utils/dateFormat';
import { createAllowance, getAllowances, getPaySchedule, saveAllowanceEdits } from 'store/entities/payroll/payrollSetup/action';
import { toast } from 'react-toastify';
import usePayrollEssentials from 'hooks/usePayrollEssentials';
import { ButtonLoader } from 'components/__new/common/form';
import Maybe from 'components/__new/common/Maybe';
import { REGEX_VALUES } from 'utils/regexValues';

const formDefault = {
    name: "",
    type: "",
    description: "",
    rate_type: "",
    value: "",
    source: "",
    frequency: "",
    is_shown_on_payslip: true,
    tax_payer: false,
    month: 0,
}

const CreateAllowanceModal = (props) => {
    const { handleToggleAllowanceForm, userInfo, logger, isAllowanceFormOpen, isEditMode, setIsEditMode, allowance } = props;
    const {dispatch, useSelector, SubPageTooltip, lettersRegex, removeNullProperties} = usePayrollEssentials()
    const [form, setForm] = useState(formDefault);

    const [isTaxable, setIsTaxable] = useState(false);
    const {getPayScheduleLoading, isAllowanceModalOpen, createAllowanceLoading: loading, editAllowanceLoading} = useSelector(state => state.entities.payrollSetup)

    // const monthlyItem = schedules?.data?.find(item => item?.frequency?.payroll_frequency === 'MONTHLY')
    // const weeklyItem = schedules?.data?.find(item => item?.frequency?.payroll_frequency === 'WEEKLY')

    // const [companyTaxed, setCompanyTaxed] = useState(true);
    // const [empTaxed, setEmpTaxed] = useState(false);

    const isPercentRate = form?.rate_type?.toLowerCase() === 'percentage'
    const deductPercent = form?.rate_type?.toLowerCase() === 'percentage'
    const isAnnual = form?.frequency === 'ANNUALLY'

    const allowanceTypes = [
        {title: "Bonus", value: "BONUS"},
        {title: "Backpay", value: "BACKPAY"},
        {title: "Cooperative", value: "COOPERATIVE"},
        {title: "Reimbursement", value: "REIMBURSEMENT"}
    ]
    
    const isSubmitBtnDisabled = useMemo(() => {
        return !form.name || !form.type || !form.description || !form.rate_type || !form.value || !form.source || !form.frequency || !form.month;
    }, [form])
    
    const handleCloseAllowanceForm = () => {
        setIsEditMode(false);
        handleToggleAllowanceForm();   
    }

    const onSubmit = (e) => {
        e.preventDefault()
        const value = form.value

        if((deductPercent && value > 100 ) || (deductPercent && value < 1)) {
            toast.error('Rate must be between 1 - 100')
        } else if(!deductPercent && value < 1) {
            toast.error('Rate must be at least ₦1')
        }  else if(!lettersRegex.test(form?.name)) {
            toast.error('Allowance name must contain letters')
        } else {
            const payload = {
                name: form.name,
                description: form.description,
                frequency: form.frequency,
                is_shown_on_payslip: form.is_shown_on_payslip,
                month: isAnnual ? Number(form.month) : null,
                rate_type: form.rate_type,
                source: form.source,
                value: Number(form.value),
                tax_payer: form.tax_payer ? "EMPLOYEE" : null,
                type: form.type,
                is_taxable: form.tax_payer ? true : false,
            }
            
            if (!isEditMode) {
                logger("Employer-Payroll-setup-allowance-create-success", userInfo);
                dispatch(createAllowance(removeNullProperties(payload))).then(() => {
                    handleCloseAllowanceForm();
                    e.target.reset();
                    dispatch(getAllowances({ "page": 1, "limit": 10 }));
                })
            } else {
                dispatch(saveAllowanceEdits({
                    ...payload,
                    id: allowance.id,
                })).then(() => {
                    handleCloseAllowanceForm();
                    dispatch(getAllowances({ "page": 1, "limit": 10 }));
                })
            }
        }
    }

    // const toggleRadios = () => {
    //     setCompanyTaxed(!companyTaxed)
    //     setEmpTaxed(!empTaxed)
    // }

    const handleChange = (evt) => {
        const { name, value } = evt.target;
                
        if (name === "name" && (!REGEX_VALUES.LETTERS_WITH_NUMBERS_AND_HYPHEN_ONLY.test(value) || value.length > 50)) {
            return;
        }
        setForm({
            ...form, 
            [name]: value,
        });
    }
    
    useEffect(() => {
        if (isAllowanceFormOpen){
            let data = {...formDefault};
            if (isEditMode){
                data = {
                    ...formDefault,
                    name: allowance?.name || "",
                    type: allowance?.type || "",
                    description: allowance?.description || "",
                    rate_type: allowance?.rate_type || "",
                    value: allowance?.value || "",
                    source: allowance?.source || "",
                    frequency: allowance?.frequency || "",
                    is_shown_on_payslip: allowance?.is_shown_on_payslip || false,
                    tax_payer: allowance?.tax_payer || "",
                    month: allowance?.month,
                };
                setForm(data);
            } else{
                setForm(formDefault);
            }
        }
        // eslint-disable-next-line
    }, [isAllowanceFormOpen]);

    useEffect(() => {
        dispatch(getPaySchedule());
        // eslint-disable-next-line
    }, [])

    return (
        <FormDrawer display="center" size="lg" isOpen={isAllowanceFormOpen} setIsOpen={handleCloseAllowanceForm}>
            <form onSubmit={onSubmit}>
                <div className="p-7">
                    <div className="mb-5">
                        <div className="mb-1 font-semibold flex items-center">
                            {isEditMode ? "Edit" : "Create"} Allowance
                            <SubPageTooltip
                                text={<span>This payroll setup is required for <br />Gross Payroll.</span>}
                            />
                        </div>
                    </div>
                    <div className="mb-5">
                        <p className="text-sm mb-2">Name of Allowance <span className="form-input-required">*</span></p>
                        <input minLength={3} maxLength={50} type="text" placeholder='Enter name' required onChange={handleChange} name='name' className="form-input" value={form.name} />
                    </div>

                    <div className="mb-5">
                        <p className="text-sm mb-1">Allowance Type <span className="form-input-required">*</span></p>
                        <select name="type" required onChange={handleChange} className="form-input" value={form.type}>
                            <option value="">Select type</option>
                            {allowanceTypes.map(item => <option value={item.value} key={item.value}>
                                {item.title}
                            </option>)}
                        </select>
                    </div>

                    <div className="mb-5">
                        <p className="text-sm mb-1">Description <span className="form-input-required">*</span></p>
                        <textarea style={{resize: "none"}} required rows="3" className="form-input-textarea" placeholder='Enter description' name='description' onChange={handleChange} value={form.description} />
                    </div>
                    <hr />

                    <div className="mb-5 pt-3">
                        <p className="text-sm mb-2">Rate <span className="form-input-required">*</span></p>
                        <div className="flex-btw items-center">
                            <div className="w-2/5 mr-3">
                                <select name="rate_type" required onChange={handleChange} className="form-input" value={form.rate_type}>
                                    <option value="">Select rate</option>
                                    <option value="PERCENTAGE">Percentage</option>
                                    <option value="FIXED_AMOUNT">Fixed Amount (₦)</option>
                                </select>
                            </div>
                            <div className="w-3/5">
                                <input type="number" placeholder='Enter Value' name='value'
                                required onChange={handleChange} className="form-input" id="prl-input" value={form.value} />
                            </div>
                        </div>
                        {isPercentRate && <p style={{fontSize: '9px'}} className="mb-1 mt-1 text-ep-primary-600">* Percentage of Gross</p>}
                    </div>

                    <div className="mb-5">
                        <p className="text-sm mb-1">Rate Source <span className="form-input-required">*</span></p>
                        <select name="source" required onChange={handleChange} className="form-input" value={form.source}>
                            <option value="">Select type</option>
                            <option value="BASIC">Basic</option>
                            <option value="HOUSING">Housing</option>
                            <option value="TRANSPORT">Transport</option>
                            <option value="BASIC_AND_HOUSING">Basic and Housing</option>
                            <option value="BASIC_AND_TRANSPORT">Basic and Transport</option>
                            <option value="HOUSING_AND_TRANSPORT">Housing and Transport</option>
                            <option value="BASIC_HOUSING_AND_TRANSPORT">Basic, Housing, and Transport</option>
                            <option value="GROSS_SALARY">Gross Salary</option>
                        </select>
                    </div>

                    <div className="mb-5 pt-3">
                        <p className="text-sm mb-2">Frequency <span className="form-input-required">*</span></p>
                        <div className="flex-btw items-center">
                            <div className="w-2/4 mr-3">
                                <select name="frequency" required onChange={handleChange} className="form-input" value={form.frequency}>
                                    <option value="">Select Frequency</option>
                                    <option value="ANNUALLY">Annually</option>
                                    <option disabled value="MONTHLY">Monthly</option>
                                    <option disabled value="WEEKLY">Weekly</option>
                                </select>
                            </div>
                            {isAnnual && <div className="w-2/4">
                                <select name="month" required onChange={handleChange} className="form-input" value={form.month}>
                                    <option value="">Select month</option>
                                    {monthNames.map((item,idx) => <option value={idx + 1} key={item}>{item}</option>)}
                                </select>
                            </div>}
                        </div>
                    </div>

                    <hr />
                    
                    <div className="mt-4 p-2">
                        <div className="flex items-center">
                            <div>
                                <input
                                    type="checkbox"
                                    onChange={() => {
                                        setForm({
                                            ...form,
                                            tax_payer: !form.tax_payer,  
                                        })
                                    }}
                                    checked={form.tax_payer}
                                    className="w-5 h-5 mr-3.5 form-checkbox float-left"
                                />
                            </div>
                            <p className="mb-0 text-ep-primary-600 text-sm">Taxable</p>
                            {/* <div>
                                <CustomSwitch active={form.tax_payer} activeText="Yes"
                                inactiveText="No" onChange={() => {
                                    setForm({
                                        ...form,
                                        tax_payer: !form.tax_payer,  
                                    })
                                }} />
                            </div> */}
                        </div>

                        <Maybe condition={form.tax_payer}>
                            <div className="flex items-center w-full mt-3">
                                {/* <div className='flex items-center mr-10'>
                                    <input type="radio" checked={companyTaxed} onChange={toggleRadios} className="w-3 h-3 form-radio rounded-full mr-2" />
                                    <p style={{fontSize: 10}} className='mb-0'>Company</p>
                                </div> */}
                                <div className='flex items-center'>
                                    <input type="radio" onChange={() => null} checked={form.tax_payer || false} className="w-3 h-3 form-radio rounded-full mr-2" />
                                    <p style={{fontSize: 10}} className='mb-0'>Employee</p>
                                </div>
                            </div>
                        </Maybe>
                    </div>

                    <div className="mb-5 flex-btw p-2 items-center">
                        <p className="mb-0 text-ep-primary-600 text-sm">Show on payslip?</p>
                        <div>
                            <CustomSwitch 
                                active={form.is_shown_on_payslip} 
                                activeText="Yes"
                                inactiveText="No" 
                                onChange={() => setForm({...form, is_shown_on_payslip: !form.is_shown_on_payslip})} 
                            />
                        </div>
                    </div>
                
                    <hr />
                </div>

                <div className="w-full flex items-center mb-4 p-7 pb-2 pt-0 justify-end">
                    <button disabled={getPayScheduleLoading || loading || editAllowanceLoading} onClick={handleCloseAllowanceForm} type='button' className="btn btn-md btn-transparent-primary mr-4 lg:mr-6">
                        Cancel
                    </button>

                    <ButtonLoader loading={loading || editAllowanceLoading} disabled={getPayScheduleLoading || isSubmitBtnDisabled} type="submit" className="btn btn-md btn-ep-primary">
                        {(loading || editAllowanceLoading) ? "Configuring" : `${isEditMode ? "Edit" : "Create"} Allowance and Bonus`}
                    </ButtonLoader>
                </div>
            </form>
        </FormDrawer>
    )
}

export default CreateAllowanceModal